import React, { useId, useMemo } from "react";

const BackgroundBlur = ({
  position = "top-right",
  colors = [],
  opacity = 0.2,
  hideOnMobile = false,
  filterId,
  heightVh = 100,
}) => {
  const positionClasses = {
    "top-right": "top-0 right-0",
    "top-left": "top-0 left-0",
    "bottom-left": "left-0 bottom-0",
    "bottom-right": "right-0 bottom-0",
  };

  const colorSchemes = {
    blue: [
      "var(--color-primary-100)",
      "var(--color-primary-200)",
      "var(--color-accent-100)",
    ],
    pink: ["var(--color-pink)", "var(--color-amber)"],
  };

  const configs = {
    "top-right": {
      width: 602,
      height: 1154,
      viewBox: "0 0 602 1154",
      circle: { cx: 577, cy: 577, r: 317 },
      blur: 130,
      gradient: { x1: 183.787, y1: 894, x2: 970.173, y2: 346.491 },
    },
    "bottom-left": {
      width: 622,
      height: 1236,
      viewBox: "0 0 622 1236",
      circle: { cx: 4, cy: 618, r: 368 },
      blur: 125,
      gradient: { x1: -364, y1: 250, x2: 506.12, y2: 754.835 },
    },
    "top-left": {
      width: 602,
      height: 1154,
      viewBox: "0 0 602 1154",
      circle: { cx: 25, cy: 577, r: 317 },
      blur: 130,
      gradient: { x1: 183.787, y1: 894, x2: 970.173, y2: 346.491 },
    },
    "bottom-right": {
      width: 622,
      height: 1236,
      viewBox: "0 0 622 1236",
      circle: { cx: 618, cy: 618, r: 368 },
      blur: 125,
      gradient: { x1: -364, y1: 250, x2: 506.12, y2: 754.835 },
    },
  };

  const config = configs[position] || configs["top-right"];

  const gradient = useMemo(() => {
    if (colors.length > 0) return colors;
    return position === "bottom-left" ? colorSchemes.pink : colorSchemes.blue;
  }, [colors, position, colorSchemes.blue, colorSchemes.pink]);

  const reactId = useId();
  const uniqueFilterId =
    filterId || `filter_${position.replace("-", "_")}_${reactId}`;
  const uniqueGradientId = `gradient_${position.replace("-", "_")}_${reactId}`;

  // Responsive, tall-safe wrapper: fixed aspect ratio, height in vh, width clamps to viewport
  const wrapperStyle = useMemo(
    () => ({
      height: `${heightVh}vh`,
      maxHeight: "100vh",
      maxWidth: "100vw",
      width: "auto",
      aspectRatio: `${config.width} / ${config.height}`,
      overflow: "hidden",
      contain: "layout style paint",
    }),
    [config.width, config.height, heightVh],
  );

  // Tight user-space filter box around the blurred circle to preserve smoothness without clipping
  const { cx, cy, r } = config.circle;
  const pad = config.blur * 2;
  const fx = cx - r - pad;
  const fy = cy - r - pad;
  const fw = r * 2 + pad * 2;
  const fh = r * 2 + pad * 2;

  return (
    <div
      className={`absolute -z-10 pointer-events-none ${positionClasses[position]} ${
        hideOnMobile ? "hidden md:block" : ""
      }`}
      aria-hidden="true"
      style={wrapperStyle}
    >
      <svg
        viewBox={config.viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
        role="presentation"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        <g opacity={opacity} filter={`url(#${uniqueFilterId})`}>
          <circle cx={cx} cy={cy} r={r} fill={`url(#${uniqueGradientId})`} />
        </g>
        <defs>
          <filter
            id={uniqueFilterId}
            x={fx}
            y={fy}
            width={fw}
            height={fh}
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation={config.blur}
              result={`effect1_foregroundBlur_${position}`}
            />
          </filter>
          <linearGradient
            id={uniqueGradientId}
            x1={config.gradient.x1}
            y1={config.gradient.y1}
            x2={config.gradient.x2}
            y2={config.gradient.y2}
            gradientUnits="userSpaceOnUse"
          >
            {gradient.map((color, index) => {
              const offset =
                gradient.length === 2
                  ? index === 0
                    ? "0"
                    : "1"
                  : index === 0
                    ? "0"
                    : index === 1
                      ? "0.541667"
                      : "1";
              return (
                <stop
                  key={`${index}-${color}`}
                  offset={offset}
                  stopColor={color}
                />
              );
            })}
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default BackgroundBlur;
